import React from "react";
import {
  ContentCopy,
  Fullscreen,
  FullscreenExit,
  NavigateBefore,
  NavigateNext,
  Pause,
  PlayArrow,
  Tune,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { SplitMode, SplitPageState } from "../../../utils/splitPageUtils";

import { SongContentToggles, SongContentTogglesProps } from "./SongContentToggles";

interface SongControlsMobileProps extends SongContentTogglesProps {
  isScrolling: boolean;
  scrollSpeed: number[];
  onToggleScroll: () => void;
  splitMode: SplitMode;
  onSplitModeChange: (
    event: React.MouseEvent<HTMLElement> | null,
    newMode: SplitMode | null
  ) => void;
  splitPageState: SplitPageState;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  mobileSettingsOpen: boolean;
  onToggleMobileSettings: () => void;
  onIncreaseFont: () => void;
  onDecreaseFont: () => void;
  onCopyChords: () => void;
  onSpeedChange: (e: Event | null, newVal: number | number[]) => void;
  showSplitIndicators?: boolean;
  onToggleSplitIndicators?: () => void;
}

export const SongControlsMobile: React.FC<SongControlsMobileProps> = ({
  visibleContent,
  onVisibleContentChange,
  isScrolling,
  scrollSpeed,
  onToggleScroll,
  splitMode,
  onSplitModeChange,
  splitPageState,
  totalPages,
  onPrevPage,
  onNextPage,
  isFullscreen,
  onToggleFullscreen,
  mobileSettingsOpen,
  onToggleMobileSettings,
  onIncreaseFont,
  onDecreaseFont,
  onCopyChords,
  onSpeedChange,
  showSplitIndicators = true,
  onToggleSplitIndicators,
}) => {
  return (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        p: 0.75,
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {/* Auto Scroll toggle & status */}
        <Button
          size="small"
          variant={isScrolling ? "contained" : "outlined"}
          color={isScrolling ? "primary" : "inherit"}
          onClick={onToggleScroll}
          startIcon={isScrolling ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
            borderRadius: 2,
            py: 0.4,
            px: 1,
          }}
        >
          {isScrolling ? `${scrollSpeed[0].toFixed(2)}x (Pause)` : "Auto-Scroll"}
        </Button>

        {/* Split pagination pills */}
        <Box display="flex" alignItems="center" gap={0.25}>
          <IconButton size="small" onClick={onPrevPage} aria-label="Previous Page" sx={{ p: 0.25 }}>
            <NavigateBefore fontSize="small" />
          </IconButton>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={
              splitMode === 1
                ? `P.${splitPageState.pages[0]}/${totalPages}`
                : `${splitPageState.pages.join("&")}/${totalPages}`
            }
            sx={{ fontWeight: "bold", fontSize: "0.7rem", height: 22 }}
          />
          <IconButton size="small" onClick={onNextPage} aria-label="Next Page" sx={{ p: 0.25 }}>
            <NavigateNext fontSize="small" />
          </IconButton>
        </Box>

        {/* Right mobile controls: Fullscreen & Settings Gear */}
        <Box display="flex" alignItems="center" gap={0.25}>
          <IconButton size="small" onClick={onToggleFullscreen} title="Toggle Fullscreen" sx={{ p: 0.5 }}>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={onToggleMobileSettings}
            title="Toggle Song Options"
            sx={{
              p: 0.5,
              bgcolor: mobileSettingsOpen ? "#eff6ff" : "transparent",
              border: "1px solid",
              borderColor: mobileSettingsOpen ? "primary.main" : "#e2e8f0",
            }}
          >
            <Tune fontSize="small" color={mobileSettingsOpen ? "primary" : "inherit"} />
          </IconButton>
        </Box>
      </Box>

      {/* Collapsible Mobile Options Drawer / Secondary Row */}
      <Collapse in={mobileSettingsOpen}>
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {/* Split Columns & Font Size Row */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                COLUMNS:
              </Typography>
              <ToggleButtonGroup
                value={splitMode}
                exclusive
                size="small"
                onChange={onSplitModeChange}
                sx={{ height: 26 }}
              >
                <ToggleButton value={1} sx={{ px: 1, py: 0 }}>
                  1
                </ToggleButton>
                <ToggleButton value={2} sx={{ px: 1, py: 0 }}>
                  2
                </ToggleButton>
                <ToggleButton value={3} sx={{ px: 1, py: 0 }}>
                  3
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                FONT:
              </Typography>
              <IconButton
                onClick={onDecreaseFont}
                size="small"
                sx={{ p: 0.3, border: "1px solid #cbd5e1", borderRadius: 1 }}
              >
                <Typography fontWeight="bold" fontSize="0.75rem">
                  A-
                </Typography>
              </IconButton>
              <IconButton
                onClick={onIncreaseFont}
                size="small"
                sx={{ p: 0.3, border: "1px solid #cbd5e1", borderRadius: 1 }}
              >
                <Typography fontWeight="bold" fontSize="0.75rem">
                  A+
                </Typography>
              </IconButton>
              <IconButton
                onClick={onCopyChords}
                size="small"
                sx={{ p: 0.3, border: "1px solid #cbd5e1", borderRadius: 1, ml: 0.5 }}
              >
                <ContentCopy sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.75}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              CONTENT:
            </Typography>
            <SongContentToggles visibleContent={visibleContent} onVisibleContentChange={onVisibleContentChange} />
          </Box>

          {/* Split Indicators Toggle Row */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              SPLIT INDICATORS:
            </Typography>
            <Switch
              size="small"
              checked={Boolean(showSplitIndicators)}
              onChange={onToggleSplitIndicators}
              inputProps={{ "aria-label": "Toggle Split Indicators Mobile" }}
              data-testid="toggle-split-indicators-mobile"
            />
          </Box>

          {/* Scroll Speed Slider Row */}
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: 42 }}>
              SPEED:
            </Typography>
            <Slider
              value={scrollSpeed}
              onChange={onSpeedChange}
              min={0.01}
              max={1.0}
              step={0.01}
              size="small"
              sx={{ flex: 1 }}
            />
            <Chip
              label={`${scrollSpeed[0].toFixed(2)}x`}
              size="small"
              color="primary"
              sx={{ fontWeight: "bold", minWidth: 46, height: 22, fontSize: "0.75rem" }}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
